import { useCallback, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  getNavigatorRangeBounds,
  getNavigatorRangeTitle,
  moveNavigatorFocus,
  normalizeYmd,
} from "../utils/timeNavigatorRange";

const BOUNDED_NAV_VIEWS = new Set(["day", "week", "month", "future"]);

export function useGlobalTimeNavigator({
  initialView = "week",
  initialFocusDateYmd,
} = {}) {
  const [view, setViewState] = useState(() =>
    ["day", "week", "month", "future", "all"].includes(initialView) ? initialView : "week"
  );
  const [focusDateYmd, setFocusDateYmd] = useState(() =>
    normalizeYmd(initialFocusDateYmd || dayjs().format("YYYY-MM-DD"))
  );

  /** When leaving All Time for a bounded range, anchor on today (matches Events UX). */
  const setView = useCallback((nextView) => {
    const v = ["day", "week", "month", "future", "all"].includes(nextView) ? nextView : "week";
    setViewState((prevView) => {
      if (BOUNDED_NAV_VIEWS.has(v) && prevView === "all") {
        setFocusDateYmd(dayjs().format("YYYY-MM-DD"));
      }
      return v;
    });
  }, []);

  const title = useMemo(
    () => getNavigatorRangeTitle({ view, focusDateYmd }),
    [view, focusDateYmd]
  );

  const bounds = useMemo(
    () => getNavigatorRangeBounds({ view, focusDateYmd }),
    [view, focusDateYmd]
  );

  const goToday = useCallback(() => {
    setViewState((prev) => (prev === "all" ? "month" : prev));
    setFocusDateYmd(dayjs().format("YYYY-MM-DD"));
  }, []);

  const movePrev = useCallback(() => {
    setFocusDateYmd((prev) =>
      moveNavigatorFocus({ view, focusDateYmd: prev, dir: "prev" })
    );
  }, [view]);

  const moveNext = useCallback(() => {
    setFocusDateYmd((prev) =>
      moveNavigatorFocus({ view, focusDateYmd: prev, dir: "next" })
    );
  }, [view]);

  return {
    view,
    setView,
    focusDateYmd,
    setFocusDateYmd,
    title,
    bounds, // { startInclusive, endInclusive } ISO strings
    goToday,
    movePrev,
    moveNext,
  };
}

